import { brand } from "../lib/brand";
import { useEffect, useState } from "react";
import { api } from "../api";
import { uploadsUrl } from "../lib/uploader";
import {
  SectionHeading,
  Loading,
  EmptyState,
  ErrorBanner,
} from "../components/ui";

interface Product {
  id: string;
  title: string;
  companyName: string | null;
  category: string | null;
  description: string | null;
  price: string | null;
  thumbnailKey: string | null;
  externalUrl: string | null;
}

export function ProductsPage() {
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [requested, setRequested] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ items: Product[] }>("/member/products")
      .then((r) => setItems(r.items))
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const apply = async (id: string) => {
    setErr(null);
    setBusyId(id);
    try {
      await api.post(`/member/products/${id}/inquiry`);
      setRequested((p) => ({ ...p, [id]: true }));
    } catch (e) {
      setErr((e as Error).message ?? "申込に失敗しました");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <SectionHeading level={2}>{brand.shortName}おすすめ商材</SectionHeading>
      <p className="text-sm text-tora-ink/70 mt-1 mb-4">
        {brand.shortName}が厳選した商材・サービスです。気になるものは「申込」を押すと、運営から個別にご案内します。
      </p>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState icon="🛍" title="現在ご紹介できる商材はありません" />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((p) => {
            const thumb = uploadsUrl(p.thumbnailKey);
            const done = requested[p.id];
            return (
              <li key={p.id} className="card p-5 flex flex-col">
                {thumb && (
                  <img
                    src={thumb}
                    alt=""
                    className="w-full aspect-[16/9] object-contain rounded-lg border border-black/10 bg-black/5 mb-3"
                  />
                )}
                {p.category && (
                  <span className="text-xs font-semibold text-tora">
                    {p.category}
                  </span>
                )}
                <h3 className="text-lg font-bold text-tora-ink leading-snug">
                  {p.title}
                </h3>
                {p.companyName && (
                  <p className="text-xs text-tora-ink/60 mt-0.5">
                    {p.companyName}
                  </p>
                )}
                {p.description && (
                  <p className="text-sm text-tora-ink/70 mt-2 whitespace-pre-wrap">
                    {p.description}
                  </p>
                )}
                {p.price && (
                  <p className="mt-2 text-sm font-bold text-tora-ink">
                    {p.price}
                  </p>
                )}
                <div className="mt-auto pt-4 flex items-center gap-3">
                  {done ? (
                    <span className="text-sm font-semibold text-green-700">
                      申込を受け付けました ✓
                    </span>
                  ) : (
                    <button
                      className="btn-primary"
                      disabled={busyId === p.id}
                      onClick={() => apply(p.id)}
                    >
                      {busyId === p.id ? "送信中…" : "申込（運営につなぐ）"}
                    </button>
                  )}
                  {p.externalUrl && (
                    <a
                      href={p.externalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-tora hover:underline"
                    >
                      詳しく見る ↗
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
